import { Typography } from "@mui/material";

/**
 * Check skill type and return text status
 * @param {number} year - Expiry year of the skill
 * @returns {string} Status text
 */
export function checkSkillType(year) {
  const currentYear = new Date().getFullYear();
  const minYearDiff = 5;
  const minYearForCurrentSkill = 1;
  const minYearForOutdated = 4;
  if (year > currentYear) {
    return `Skill trending and in demand until ${year}`;
  } else if (year >= currentYear - minYearForCurrentSkill) {
    return `Skill current and in demand until ${year}`;
  } else if (year >= currentYear - minYearForOutdated) {
    return `Skill getting outdated fast ${year}`;
  } else if (year <= currentYear - minYearDiff) {
    return `Skill obselete ${year}`;
  }
}

/**
 * Check year and return status object with colors and label suggestion
 * @param {number} year - Expiry year of the skill
 * @returns {object} Status object with label, colors, and labelSuggestion (JSX)
 */
export function checkYear(year) {
  const currentYear = new Date().getFullYear();
  const minYearDiff = 5;
  const minYearForOutdatedSkill = 4;
  const minYearForCurrentSkill = 1;

  if (year > currentYear) {
    const labelSuggestion = (
      <Typography
        sx={{
          fontFamily: "Montserrat",
          color: "#232323",
        }}
        variant="h6"
      >
        <span
          style={{
            fontWeight: 700,
            color: "#26801E",
          }}
        >
          Trending{" "}
        </span>{" "}
        : You are in demand, but keep upgrading
      </Typography>
    );
    return {
      label: `Technical skill trending and in demand until ${year}`,
      barPrimaryColor: "#278C37",
      barSecondaryColor: "#9ad7a3",
      labelSuggestion,
      percentageColor: "#278C37",
    };
  } else if (year >= currentYear - minYearForCurrentSkill) {
    const labelSuggestion = (
      <Typography
        sx={{
          fontFamily: "Montserrat",
          fontSize: "20px",
          color: "#232323",
          mb: "47px",
        }}
      >
        <span
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: "darkorange",
          }}
        >
          • In demand
        </span>{" "}
        <span>: Your skill is in demand, but it’s getting outdated fast.</span>
      </Typography>
    );
    return {
      label: `Technical skill in demand until ${year}`,
      barPrimaryColor: "#E9B112",
      barSecondaryColor: "#f5e7be",
      labelSuggestion,
      percentageColor: "#E9B112",
    };
  } else if (year >= currentYear - minYearForOutdatedSkill) {
    const labelSuggestion = (
      <Typography
        sx={{
          fontFamily: "Montserrat",
          fontSize: "20px",
          color: "#232323",
          mb: "47px",
        }}
      >
        <span
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#ffc107",
          }}
        >
          • Getting outdated
        </span>{" "}
        <span>: Upskill soon to stay ahead in the industry.</span>
      </Typography>
    );
    return {
      label: `Technical skill getting outdated fast ${year}`,
      barPrimaryColor: "#C60015",
      barSecondaryColor: "#ffa7b0",
      labelSuggestion,
      percentageColor: "#C60015",
    };
  } else if (year <= currentYear - minYearDiff) {
    const labelSuggestion = (
      <Typography
        sx={{
          fontFamily: "Montserrat",
          fontSize: "24px",
          color: "#232323",
          mb: "47px",
        }}
      >
        <span
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: "red",
          }}
        >
          • Obsolete
        </span>{" "}
        : Please learn new age skills now
      </Typography>
    );
    return {
      label: `Technical skill obselete ${year}`,
      barPrimaryColor: "#028EDF",
      barSecondaryColor: "#b7e5ff",
      labelSuggestion,
      percentageColor: "#028EDF",
    };
  }
}

/**
 * Get the top skill from an array based on expiry year
 * @param {Array} skillArray - Array of skill objects
 * @returns {object} Object with maxExpiryYear, labelSuggestion, and maxExpiryYearObject
 */
export function getTopSkill(skillArray) {
  let maxExpiryYear = -Infinity;
  let maxExpiryYearObject = null;

  // Iterate through the array
  for (const obj of skillArray) {
    if (
      obj.expiryYear > maxExpiryYear &&
      obj.expiryYear !== null &&
      obj.expiryYear < new Date().getFullYear() + 25
    ) {
      maxExpiryYear = obj.expiryYear;
      maxExpiryYearObject = obj;
    } else {
      if (obj?.expiryYear > new Date().getFullYear() + 100) {
        maxExpiryYear = parseFloat(obj?.estYear) + 10;
        maxExpiryYearObject = {
          ...obj,
          expiryYear: parseFloat(obj?.estYear) + 15,
        };
      } else if (obj?.estYear === null || obj.estYear === undefined) {
        maxExpiryYear = new Date().getFullYear() + 2;
        maxExpiryYearObject = {
          ...obj,
          expiryYear: new Date().getFullYear() + 2,
        };
      }
    }
  }
  const topSkillSuggestion = checkYear(maxExpiryYearObject?.expiryYear);

  return {
    maxExpiryYear,
    labelSuggestion: topSkillSuggestion?.labelSuggestion,
    maxExpiryYearObject,
  };
}

/**
 * Encrypt data for API submission
 * @param {object} json - Data object to encrypt
 * @param {string} secretEncodeDecodeString - Secret string from feature flag
 * @param {string} staticRandomEncodeDecodeString - Static string from feature flag
 * @returns {string} Base64 encoded encrypted string
 */
export function DataEncryption(json, secretEncodeDecodeString, staticRandomEncodeDecodeString) {
  const jsonString = JSON.stringify(json);
  const customStringJson =
    secretEncodeDecodeString + jsonString + secretEncodeDecodeString;
  const newCustomStringJson = customStringJson.replace(
    /"/g,
    staticRandomEncodeDecodeString
  );

  // Convert the string to UTF-8 encoding
  const utf8EncodedString = new TextEncoder().encode(newCustomStringJson);

  // Convert the UTF-8 array to a string in chunks
  let utf8String = "";
  for (let i = 0; i < utf8EncodedString.length; i += 10000) {
    utf8String += String.fromCharCode(
      ...utf8EncodedString.slice(i, i + 10000)
    );
  }

  const base64EncodedString = btoa(utf8String);
  return base64EncodedString;
}

/**
 * Decrypt data from API response
 * @param {string} jsonString - Encrypted base64 string
 * @param {string} secretEncodeDecodeString - Secret string from feature flag
 * @param {string} staticRandomEncodeDecodeString - Static string from feature flag
 * @returns {object} Decrypted JSON object
 */
export function DataDecryption(jsonString, secretEncodeDecodeString, staticRandomEncodeDecodeString) {
  const decodedString = atob(jsonString);
  const getOldStringOne = decodedString.replace(
    new RegExp(secretEncodeDecodeString, "g"),
    ""
  );
  const str = staticRandomEncodeDecodeString;
  const escapedString = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escapedString, "g");
  const getOldStringTwo = getOldStringOne.replace(regex, '"');
  const json = JSON.parse(getOldStringTwo);
  return json;
}

